import { useQuery } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { api, jsonBody } from "../../api/client";
import type { PlatformRole } from "../../api/types";
import { formatDate, formatMoney, titleCase } from "../../lib/format";
import {
  Card,
  CardHeader,
  DataTable,
  EmptyState,
  ErrorPanel,
  Field,
  FormActions,
  Metric,
  Notice,
  StatusPill,
} from "../../ui/components";

export type ControlPlaneSection = "overview" | "billing" | "reliability" | "notices";

interface TenantOption {
  id: string;
  displayName: string;
}
interface PlatformOverview {
  tenants: number;
  activeTenants: number;
  tenantsCreatedLast30Days: number;
  users: number;
  venues: number;
  bookings: number;
  bookingsCreatedLast30Days: number;
  monthlyRecurringRevenueMinor: number;
  jobsByState: Record<string, number>;
  deliveriesByState: Record<string, number>;
  domainsByState: Record<string, number>;
  subscriptionsByState: Record<string, number>;
  onboardingByState: Record<string, number>;
  connectedAccountsByState: Record<string, number>;
  adjustmentsByState: Record<string, number>;
  generatedAt: string;
}
interface Plan {
  code: string;
  name: string;
  features: Record<string, unknown>;
  limits: Record<string, unknown>;
  active: boolean;
}
interface Entitlement {
  tenantId: string;
  planCode: string;
  planName: string;
  features: Record<string, unknown>;
  limits: Record<string, unknown>;
  featureOverrides: Record<string, unknown>;
  limitOverrides: Record<string, unknown>;
  version: number;
  updatedAt: string;
}
interface CapacityRequest {
  id: string;
  tenantId: string;
  requestedLimits: Record<string, number>;
  requestedFeatures: Record<string, boolean>;
  context: string;
  status: "requested" | "reviewing" | "quoted" | "accepted" | "declined" | "cancelled";
  quotedMonthlyPriceMinor?: number;
  quotedAnnualPriceMinor?: number;
  operatorReason?: string;
  version: number;
  createdAt: string;
  updatedAt: string;
}
interface DeadJob {
  id: string;
  tenantId?: string;
  type: string;
  attempts: number;
  maxAttempts: number;
  lastError: string;
  deadLetteredAt: string;
}
interface Incident {
  id: string;
  title: string;
  body: string;
  severity: string;
  status: string;
  startedAt: string;
  resolvedAt?: string;
  version: number;
  updatedAt: string;
}
interface ServiceNotice {
  id: string;
  title: string;
  body: string;
  tone: string;
  audience: string;
  activeFrom: string;
  activeUntil?: string;
  published: boolean;
  version: number;
  updatedAt: string;
}
interface SensitiveState {
  unlocked: boolean;
  markProtectedError(error: unknown): boolean;
}

function roleCapabilities(role: PlatformRole) {
  return {
    billing: role === "platform_owner" || role === "billing_operator",
    securityView: role === "platform_owner" || role === "security_operator" || role === "auditor",
    securityManage: role === "platform_owner" || role === "security_operator",
    tenantsManage: role === "platform_owner",
  };
}

function Breakdown({ title, values }: { title: string; values: Record<string, number> }) {
  const entries = Object.entries(values);
  return (
    <Card>
      <CardHeader title={title} />
      {entries.length ? (
        <dl className="definition-list">
          {entries.flatMap(([key, value]) => [
            <dt key={`${key}-k`}>{titleCase(key)}</dt>,
            <dd key={`${key}-v`}>{value}</dd>,
          ])}
        </dl>
      ) : (
        <p className="muted">No records.</p>
      )}
    </Card>
  );
}

export function ControlPlane({
  tenants,
  role,
  sensitive,
  section,
  tenantId,
  onTenantChange,
}: {
  tenants: TenantOption[];
  role: PlatformRole;
  sensitive: SensitiveState;
  section: ControlPlaneSection;
  tenantId: string;
  onTenantChange(tenantId: string): void;
}) {
  const capabilities = roleCapabilities(role);
  const [error, setError] = useState<unknown>();
  const [working, setWorking] = useState(false);
  const [jobReasons, setJobReasons] = useState<Record<string, string>>({});
  const [incident, setIncident] = useState<Incident>();
  const [notice, setNotice] = useState<ServiceNotice>();
  const overview = useQuery({
    queryKey: ["operator", "overview"],
    queryFn: () => api<PlatformOverview>("/api/v1/operator/overview"),
    enabled: section === "overview",
  });
  const plans = useQuery({
    queryKey: ["operator", "plans"],
    queryFn: () => api<{ items: Plan[] }>("/api/v1/operator/plans"),
    enabled: section === "billing",
  });
  const capacityRequests = useQuery({
    queryKey: ["operator", "capacity-requests"],
    queryFn: () => api<{ items: CapacityRequest[] }>("/api/v1/operator/capacity-requests"),
    enabled: section === "billing" && capabilities.billing && sensitive.unlocked,
  });
  const entitlement = useQuery({
    queryKey: ["operator", "entitlement", tenantId],
    queryFn: () => api<Entitlement>(`/api/v1/operator/tenants/${tenantId}/entitlement`),
    enabled: section === "billing" && Boolean(tenantId),
  });
  const deadJobs = useQuery({
    queryKey: ["operator", "dead-jobs"],
    queryFn: () => api<{ items: DeadJob[] }>("/api/v1/operator/jobs/dead?limit=100"),
    enabled: section === "reliability" && capabilities.securityView,
  });
  const incidents = useQuery({
    queryKey: ["operator", "incidents"],
    queryFn: () => api<{ items: Incident[] }>("/api/v1/operator/incidents"),
    enabled: section === "reliability" && capabilities.securityView,
  });
  const notices = useQuery({
    queryKey: ["operator", "notices"],
    queryFn: () => api<{ items: ServiceNotice[] }>("/api/v1/operator/notices"),
    enabled: section === "notices",
  });
  const [drill, setDrill] = useState<{ requestId: string; firedAt: string }>();

  async function protectedAction(action: () => Promise<void>) {
    setWorking(true);
    setError(undefined);
    try {
      await action();
    } catch (caught) {
      if (!sensitive.markProtectedError(caught)) setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function saveEntitlement(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!entitlement.data) return;
    const form = new FormData(event.currentTarget);
    await protectedAction(async () => {
      const parseObject = (name: string) => {
        const value = JSON.parse(String(form.get(name) || "{}"));
        if (!value || Array.isArray(value) || typeof value !== "object")
          throw new Error(`${name} must be a JSON object`);
        return value;
      };
      await api(`/api/v1/operator/tenants/${tenantId}/entitlement`, {
        method: "PUT",
        body: jsonBody({
          planCode: String(form.get("planCode")),
          featureOverrides: parseObject("featureOverrides"),
          limitOverrides: parseObject("limitOverrides"),
          version: entitlement.data.version,
          reason: String(form.get("reason")),
        }),
      });
      await entitlement.refetch();
    });
  }

  async function reviewCapacityRequest(
    event: FormEvent<HTMLFormElement>,
    request: CapacityRequest,
  ) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const status = String(form.get("status"));
    const quoteRequired = status === "quoted" || status === "accepted";
    const monthly = Number(form.get("quotedMonthlyPrice"));
    const annual = Number(form.get("quotedAnnualPrice"));
    if (
      quoteRequired &&
      (!Number.isFinite(monthly) || monthly <= 0 || !Number.isFinite(annual) || annual <= 0)
    ) {
      setError(
        new Error(
          "Positive monthly and annual quote prices are required before quoting or accepting capacity.",
        ),
      );
      return;
    }
    await protectedAction(async () => {
      await api(`/api/v1/operator/capacity-requests/${request.id}`, {
        method: "PUT",
        body: jsonBody({
          status,
          quotedMonthlyPriceMinor: quoteRequired ? Math.round(monthly * 100) : null,
          quotedAnnualPriceMinor: quoteRequired ? Math.round(annual * 100) : null,
          reason: String(form.get("reason")),
          version: request.version,
        }),
      });
      await capacityRequests.refetch();
      if (status === "accepted" && request.tenantId === tenantId) await entitlement.refetch();
    });
  }

  async function retryJob(job: DeadJob) {
    await protectedAction(async () => {
      await api(`/api/v1/operator/jobs/${job.id}/retry`, {
        method: "POST",
        body: jsonBody({ reason: jobReasons[job.id] ?? "" }),
      });
      setJobReasons((current) => ({ ...current, [job.id]: "" }));
      await Promise.all([deadJobs.refetch(), overview.refetch()]);
    });
  }

  // One ERROR line, written on purpose, so the alerting path can be proved rather than trusted.
  async function sendDrill() {
    await protectedAction(async () => {
      setDrill(
        await api<{ requestId: string; firedAt: string }>("/api/v1/operator/observability/drill", {
          method: "POST",
        }),
      );
    });
  }

  async function saveIncident(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    await protectedAction(async () => {
      const id = incident?.id;
      await api(id ? `/api/v1/operator/incidents/${id}` : "/api/v1/operator/incidents", {
        method: id ? "PUT" : "POST",
        body: jsonBody({
          title: String(form.get("title")),
          body: String(form.get("body")),
          severity: String(form.get("severity")),
          status: String(form.get("status")),
          startedAt: new Date(String(form.get("startedAt"))).toISOString(),
          version: incident?.version ?? 0,
          reason: String(form.get("reason")),
        }),
      });
      setIncident(undefined);
      await incidents.refetch();
    });
  }

  async function saveNotice(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const activeUntil = String(form.get("activeUntil") || "");
    await protectedAction(async () => {
      const id = notice?.id;
      await api(id ? `/api/v1/operator/notices/${id}` : "/api/v1/operator/notices", {
        method: id ? "PUT" : "POST",
        body: jsonBody({
          title: String(form.get("title")),
          body: String(form.get("body")),
          tone: String(form.get("tone")),
          audience: String(form.get("audience")),
          activeFrom: new Date(String(form.get("activeFrom"))).toISOString(),
          activeUntil: activeUntil ? new Date(activeUntil).toISOString() : null,
          published: form.get("published") === "on",
          version: notice?.version ?? 0,
          reason: String(form.get("reason")),
        }),
      });
      setNotice(undefined);
      await notices.refetch();
    });
  }

  const queryError =
    section === "overview"
      ? overview.error
      : section === "billing"
        ? (plans.error ?? entitlement.error ?? capacityRequests.error)
        : section === "reliability"
          ? (deadJobs.error ?? incidents.error)
          : section === "notices"
            ? notices.error
            : undefined;
  return (
    <section className="stack" aria-label={`${titleCase(section)} platform controls`}>
      {error || queryError ? <ErrorPanel error={error ?? queryError} /> : null}
      {section === "overview" && overview.data ? (
        <>
          <div className="metrics-grid">
            <Metric
              label="Active customers"
              value={`${overview.data.activeTenants}/${overview.data.tenants}`}
            />
            <Metric
              label="New customers (30 days)"
              value={overview.data.tenantsCreatedLast30Days}
            />
            <Metric
              label="Monthly subscription value"
              value={formatMoney(overview.data.monthlyRecurringRevenueMinor)}
            />
            <Metric label="Users" value={overview.data.users} />
            <Metric label="Venues" value={overview.data.venues} />
            <Metric label="Bookings" value={overview.data.bookings} />
            <Metric
              label="New bookings (30 days)"
              value={overview.data.bookingsCreatedLast30Days}
            />
          </div>
          <div className="content-grid">
            <div className="span-4">
              <Breakdown title="Subscriptions" values={overview.data.subscriptionsByState} />
            </div>
            <div className="span-4">
              <Breakdown title="Customer onboarding" values={overview.data.onboardingByState} />
            </div>
            <div className="span-4">
              <Breakdown
                title="Connected payments"
                values={overview.data.connectedAccountsByState}
              />
            </div>
            <div className="span-4">
              <Breakdown title="Email deliveries" values={overview.data.deliveriesByState} />
            </div>
            <div className="span-4">
              <Breakdown title="Background jobs" values={overview.data.jobsByState} />
            </div>
            <div className="span-4">
              <Breakdown title="Billing actions" values={overview.data.adjustmentsByState} />
            </div>
            <div className="span-4">
              <Breakdown title="Custom domains" values={overview.data.domainsByState} />
            </div>
          </div>
        </>
      ) : null}
      {section === "billing" ? (
        <Card>
          <CardHeader
            title="Tenant plan and entitlements"
            description="Select a fixed plan; JSON overrides are explicit exceptions and every change requires a reason."
          />
          <Field label="Tenant">
            <select value={tenantId} onChange={(event) => onTenantChange(event.target.value)}>
              {tenants.map((tenant) => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.displayName}
                </option>
              ))}
            </select>
          </Field>
          {entitlement.data && plans.data ? (
            <form className="form-grid" onSubmit={saveEntitlement}>
              <Field label="Plan">
                <select name="planCode" defaultValue={entitlement.data.planCode}>
                  {plans.data.items
                    .filter((plan) => plan.active)
                    .map((plan) => (
                      <option key={plan.code} value={plan.code}>
                        {plan.name}
                      </option>
                    ))}
                </select>
              </Field>
              <Field label="Version">
                <input value={entitlement.data.version} readOnly />
              </Field>
              <Field label="Feature overrides" hint="JSON object only">
                <textarea
                  name="featureOverrides"
                  defaultValue={JSON.stringify(entitlement.data.featureOverrides, null, 2)}
                  rows={6}
                />
              </Field>
              <Field label="Limit overrides" hint="JSON object only">
                <textarea
                  name="limitOverrides"
                  defaultValue={JSON.stringify(entitlement.data.limitOverrides, null, 2)}
                  rows={6}
                />
              </Field>
              <Field label="Reason">
                <textarea name="reason" required minLength={10} />
              </Field>
              <div className="full">
                <FormActions
                  submitting={working}
                  disabled={!capabilities.billing || !sensitive.unlocked}
                  submitLabel="Update entitlements"
                />
              </div>
            </form>
          ) : (
            <p className="muted">Select a tenant with an entitlement record.</p>
          )}
        </Card>
      ) : null}
      {section === "billing" && capabilities.billing ? (
        <Card>
          <CardHeader
            title="Custom capacity requests"
            description="Review requested capacity, record the full customer-facing quote, and apply accepted limits atomically."
          />
          {!sensitive.unlocked ? (
            <Notice tone="warning">
              Complete a recent identity check to load or review pricing requests.
            </Notice>
          ) : capacityRequests.isLoading ? (
            <p className="muted">Loading capacity requests…</p>
          ) : capacityRequests.data?.items.length ? (
            <div className="stack">
              {capacityRequests.data.items.map((request) => {
                const tenant = tenants.find((candidate) => candidate.id === request.tenantId);
                const nextStatuses =
                  request.status === "requested"
                    ? ["reviewing", "quoted", "declined"]
                    : request.status === "reviewing"
                      ? ["quoted", "declined"]
                      : request.status === "quoted"
                        ? ["accepted", "declined"]
                        : [];
                return (
                  <div className="embedded-panel stack" key={request.id}>
                    <div className="inline spread">
                      <strong>{tenant?.displayName ?? request.tenantId}</strong>
                      <StatusPill value={request.status} />
                    </div>
                    <small>Requested {formatDate(request.createdAt)}</small>
                    <p>{request.context}</p>
                    <dl className="definition-list">
                      {Object.entries(request.requestedLimits).map(([key, value]) => [
                        <dt key={`${key}-limit-name`}>{titleCase(key)}</dt>,
                        <dd key={`${key}-limit-value`}>{value}</dd>,
                      ])}
                      {Object.entries(request.requestedFeatures).map(([key]) => [
                        <dt key={`${key}-feature-name`}>{titleCase(key)}</dt>,
                        <dd key={`${key}-feature-value`}>Included</dd>,
                      ])}
                    </dl>
                    {request.quotedMonthlyPriceMinor && request.quotedAnnualPriceMinor ? (
                      <Notice tone="info">
                        Current quote: {formatMoney(request.quotedMonthlyPriceMinor)} monthly or{" "}
                        {formatMoney(request.quotedAnnualPriceMinor)} annually.
                      </Notice>
                    ) : null}
                    {request.operatorReason ? (
                      <small>Last review: {request.operatorReason}</small>
                    ) : null}
                    {nextStatuses.length ? (
                      <form
                        className="form-grid"
                        onSubmit={(event) => reviewCapacityRequest(event, request)}
                      >
                        <Field label="Review outcome">
                          <select name="status" defaultValue={nextStatuses[0]}>
                            {nextStatuses.map((status) => (
                              <option key={status} value={status}>
                                {titleCase(status)}
                              </option>
                            ))}
                          </select>
                        </Field>
                        <Field
                          label="Monthly quote (USD)"
                          hint="Required when quoting or accepting; include all platform fees."
                        >
                          <input
                            name="quotedMonthlyPrice"
                            type="number"
                            min="0.01"
                            step="0.01"
                            defaultValue={
                              request.quotedMonthlyPriceMinor
                                ? request.quotedMonthlyPriceMinor / 100
                                : undefined
                            }
                          />
                        </Field>
                        <Field
                          label="Annual quote (USD)"
                          hint="Required when quoting or accepting; this is the full customer price."
                        >
                          <input
                            name="quotedAnnualPrice"
                            type="number"
                            min="0.01"
                            step="0.01"
                            defaultValue={
                              request.quotedAnnualPriceMinor
                                ? request.quotedAnnualPriceMinor / 100
                                : undefined
                            }
                          />
                        </Field>
                        <Field label="Audit reason">
                          <textarea name="reason" required minLength={10} />
                        </Field>
                        <div className="full">
                          <FormActions
                            submitting={working}
                            disabled={!sensitive.unlocked}
                            submitLabel="Review request"
                          />
                        </div>
                      </form>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : (
            <EmptyState
              title="No capacity requests"
              description="Customer requests for additional venues, locations, staff, or features will appear here."
            />
          )}
        </Card>
      ) : null}
      {section === "reliability" && capabilities.securityView ? (
        <Card>
          <CardHeader
            title="Dead-letter jobs"
            description="Inspect the bounded failure and supply a reason before returning a job to the queue."
          />
          {deadJobs.data?.items.length ? (
            <div className="stack">
              {deadJobs.data.items.map((job) => (
                <div className="embedded-panel stack" key={job.id}>
                  <div className="inline spread">
                    <strong>{job.type}</strong>
                    <StatusPill value="dead" />
                  </div>
                  <small>
                    {job.tenantId || "Platform job"} · {job.attempts}/{job.maxAttempts} attempts ·{" "}
                    {formatDate(job.deadLetteredAt)}
                  </small>
                  <Notice tone="danger">{job.lastError}</Notice>
                  {capabilities.securityManage ? (
                    <div className="inline">
                      <input
                        aria-label={`Retry reason for ${job.type}`}
                        value={jobReasons[job.id] ?? ""}
                        minLength={10}
                        placeholder="Reason for retry"
                        onChange={(event) =>
                          setJobReasons((current) => ({ ...current, [job.id]: event.target.value }))
                        }
                      />
                      <button
                        className="button secondary"
                        type="button"
                        disabled={
                          working ||
                          !sensitive.unlocked ||
                          (jobReasons[job.id]?.trim().length ?? 0) < 10
                        }
                        onClick={() => retryJob(job)}
                      >
                        Retry job
                      </button>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <EmptyState
              title="No dead-letter jobs"
              description="Failed work requiring intervention will appear here."
            />
          )}
        </Card>
      ) : null}
      {section === "reliability" && capabilities.securityView ? (
        <div className="content-grid">
          <Card className="span-5">
            <CardHeader title="Incidents" />
            {incidents.data?.items.length ? (
              <DataTable
                caption="Platform incidents"
                items={incidents.data.items}
                getKey={(item) => item.id}
                columns={[
                  {
                    label: "Incident",
                    render: (item) =>
                      capabilities.securityManage ? (
                        <button
                          className="button ghost small"
                          type="button"
                          onClick={() => setIncident(item)}
                        >
                          {item.title}
                        </button>
                      ) : (
                        <span>{item.title}</span>
                      ),
                  },
                  { label: "Severity", render: (item) => <StatusPill value={item.severity} /> },
                  { label: "Status", render: (item) => <StatusPill value={item.status} /> },
                ]}
              />
            ) : (
              <EmptyState
                title="No incidents"
                description="Operational incidents will be tracked here."
              />
            )}
          </Card>
          {capabilities.securityManage ? (
            <Card className="span-7">
              <CardHeader title={incident ? "Update incident" : "Create incident"} />
              <form className="form-grid" key={incident?.id ?? "new"} onSubmit={saveIncident}>
                <Field label="Title">
                  <input name="title" required defaultValue={incident?.title} />
                </Field>
                <Field label="Severity">
                  <select name="severity" defaultValue={incident?.severity ?? "minor"}>
                    <option value="info">Info</option>
                    <option value="minor">Minor</option>
                    <option value="major">Major</option>
                    <option value="critical">Critical</option>
                  </select>
                </Field>
                <Field label="Status">
                  <select name="status" defaultValue={incident?.status ?? "investigating"}>
                    <option value="investigating">Investigating</option>
                    <option value="identified">Identified</option>
                    <option value="monitoring">Monitoring</option>
                    <option value="resolved">Resolved</option>
                  </select>
                </Field>
                <Field label="Started at">
                  <input
                    name="startedAt"
                    type="datetime-local"
                    required
                    defaultValue={(incident?.startedAt ?? new Date().toISOString()).slice(0, 16)}
                  />
                </Field>
                <Field label="Public summary">
                  <textarea name="body" rows={4} defaultValue={incident?.body} />
                </Field>
                <Field label="Audit reason">
                  <textarea name="reason" required minLength={10} />
                </Field>
                <div className="full inline">
                  <FormActions
                    submitting={working}
                    disabled={!sensitive.unlocked}
                    submitLabel={incident ? "Update incident" : "Create incident"}
                  />
                  {incident ? (
                    <button
                      className="button ghost"
                      type="button"
                      onClick={() => setIncident(undefined)}
                    >
                      Start new
                    </button>
                  ) : null}
                </div>
              </form>
            </Card>
          ) : null}
        </div>
      ) : null}
      {section === "reliability" && capabilities.securityManage ? (
        <Card>
          <CardHeader
            title="Alert drill"
            description="Writes one ERROR line and nothing else, so the page and the email can be proved end to end instead of trusted. Expect the Worker error alert within a few minutes, then resolve it in Grafana."
          />
          {drill ? (
            <Notice tone="info">
              Drill sent at {formatDate(drill.firedAt)}. Request id {drill.requestId}.
            </Notice>
          ) : null}
          <div className="inline">
            <button
              className="button secondary"
              type="button"
              disabled={working || !sensitive.unlocked}
              onClick={sendDrill}
            >
              Send alert drill
            </button>
          </div>
        </Card>
      ) : null}
      {section === "notices" ? (
        <div className="content-grid">
          <Card className="span-5">
            <CardHeader title="Service notices" />
            {notices.data?.items.length ? (
              <DataTable
                caption="Service notices"
                items={notices.data.items}
                getKey={(item) => item.id}
                columns={[
                  {
                    label: "Notice",
                    render: (item) =>
                      capabilities.tenantsManage ? (
                        <button
                          className="button ghost small"
                          type="button"
                          onClick={() => setNotice(item)}
                        >
                          {item.title}
                        </button>
                      ) : (
                        <span>{item.title}</span>
                      ),
                  },
                  { label: "Audience", render: (item) => titleCase(item.audience) },
                  {
                    label: "State",
                    render: (item) => <StatusPill value={item.published ? "published" : "draft"} />,
                  },
                ]}
              />
            ) : (
              <EmptyState
                title="No service notices"
                description="Published customer notices will appear here."
              />
            )}
          </Card>
          {capabilities.tenantsManage ? (
            <Card className="span-7">
              <CardHeader title={notice ? "Update service notice" : "Create service notice"} />
              <form className="form-grid" key={notice?.id ?? "new"} onSubmit={saveNotice}>
                <Field label="Title">
                  <input name="title" required defaultValue={notice?.title} />
                </Field>
                <Field label="Tone">
                  <select name="tone" defaultValue={notice?.tone ?? "info"}>
                    <option value="info">Info</option>
                    <option value="warning">Warning</option>
                    <option value="danger">Danger</option>
                    <option value="success">Success</option>
                  </select>
                </Field>
                <Field label="Audience">
                  <select name="audience" defaultValue={notice?.audience ?? "tenants"}>
                    <option value="all">All</option>
                    <option value="tenants">Tenants</option>
                    <option value="operators">Operators</option>
                  </select>
                </Field>
                <Field label="Active from">
                  <input
                    name="activeFrom"
                    type="datetime-local"
                    required
                    defaultValue={(notice?.activeFrom ?? new Date().toISOString()).slice(0, 16)}
                  />
                </Field>
                <Field label="Active until">
                  <input
                    name="activeUntil"
                    type="datetime-local"
                    defaultValue={notice?.activeUntil?.slice(0, 16)}
                  />
                </Field>
                <Field label="Message">
                  <textarea name="body" required minLength={3} defaultValue={notice?.body} />
                </Field>
                <Field label="Audit reason">
                  <textarea name="reason" required minLength={10} />
                </Field>
                <label className="check">
                  <input name="published" type="checkbox" defaultChecked={notice?.published} />{" "}
                  Publish notice
                </label>
                <div className="full inline">
                  <FormActions
                    submitting={working}
                    disabled={!sensitive.unlocked}
                    submitLabel={notice ? "Update notice" : "Create notice"}
                  />
                  {notice ? (
                    <button
                      className="button ghost"
                      type="button"
                      onClick={() => setNotice(undefined)}
                    >
                      Start new
                    </button>
                  ) : null}
                </div>
              </form>
            </Card>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
